/**
 * Traduccion entre la consola del contador (`Filing`, ingles) y el modulo
 * `filings` del backend (espanol, `app/modules/filings/router.py`).
 *
 * Mismo criterio que `routes/masters/mapping.ts`: NO es conversion de casing.
 * `Filing.id` no es el id de una declaracion —no existe tal tabla— sino
 * `period.id`, el id del PERIODO contable; `Filing.ruc` es `entity.tax_id`;
 * `Filing.tenantName` es el nombre del tenant que el backend agrega fila a fila
 * recorriendo la cartera. Un conversor automatico no habria acertado ninguna.
 *
 * LA DIFERENCIA CONCEPTUAL, que es la que hay que tener presente al leer esto:
 * la SPA modela una DECLARACION (formulario, vencimiento, IGV a pagar, pago a
 * cuenta) y el backend modela un PERIODO CONTABLE (anio, mes, estado, quien lo
 * cerro, quien lo firmo). La declaracion es lo que se PRESENTA a partir del
 * periodo, y el backend todavia no la modela: `GET /filings/{id}/export` la
 * arma al vuelo. Por eso los cuatro importes tributarios de la pantalla no
 * tienen contraparte y se leen en cero, con su TODO.
 *
 * Las formas salen de EJECUTAR el servicio contra el backend con `X-Tenant-Id`,
 * no de leer los modelos.
 *
 * OJO CON EL TENANT: `/filings/inbox` es el UNICO endpoint cross-tenant del
 * sistema (el contador tiene cartera). Todas las demas rutas de `filings` piden
 * el tenant del PERIODO, que no es necesariamente el tenant activo de la SPA.
 * Por eso `Filing.tenantId` se conserva y `accountant/api.ts` lo manda en el
 * header de cada escritura. Mandar el tenant activo daria 403 en toda fila de
 * la cartera que no sea la del tenant seleccionado.
 */

import type {
  Filing,
  FilingBookTotal,
  FilingDetail,
  FilingDiffRow,
  FilingException,
  FilingStatus,
} from "@/lib/types"

/* ==========================================================================
 * Formas reales del backend (observadas, no deducidas)
 * ========================================================================== */

/**
 * Una fila de `GET /filings/inbox`.
 *
 * Es `period` + JOIN con `entity`, mas tres campos que el router calcula en
 * Python fila a fila (`tenant_id`, `tenant`, `periodo`, `listo_para_revisar`).
 */
export interface FilingInboxRow {
  //: `period.id`. ES el id que usan sign / observe / export / detalle
  id: string
  entity_id: string
  anio: number
  mes: number
  //: el inbox solo devuelve 'abierto' y 'en_revision' (esta en el WHERE)
  estado: string
  //: uuid de app_user que cerro el periodo — QUIEN PREPARO
  cerrado_por: string | null
  //: uuid de app_user que firmo
  firmado_por: string | null
  razon_social: string
  tax_id: string
  pais: string
  //: comprobantes del mes sin contabilizar (pendiente / en_revision)
  pendientes: number
  tenant_id: string
  tenant: string | null
  //: "AAAA-MM", compuesto por el backend
  periodo: string
  //: pendientes == 0
  listo_para_revisar: boolean
}

/** `GET /filings/{period_id}` — el detalle. Lo consume `filingDetailFromApi`. */
export interface FilingDetailRow {
  periodo: string
  entidad: { razon_social: string; tax_id: string; pais: string }
  estado: string
  preparado_por: string | null
  firmado_por: string | null
  totales: {
    por_libro: Record<string, { debe: string | number; haber: string | number; asientos: number; cuadra: boolean }>
    documentos: Record<string, number>
  }
  diff_mes_anterior: Record<
    string,
    { anterior: string | number; actual: string | number; variacion_pct: number | null; atencion: boolean }
  >
  excepciones: ExcepcionRow[]
}

/** Una excepcion, tal como la arma `_excepciones()` en el backend. */
export interface ExcepcionRow {
  //: BLOCKER | WARNING | INFO
  severidad: string
  //: LIBRO_DESCUADRADO | DOCUMENTOS_SIN_CONTABILIZAR | CONFIANZA_BAJA
  codigo: string
  mensaje: string
  detalle?: string | null
  accion?: string | null
}

/** `POST /filings/{period_id}/sign` */
export interface FirmaRow {
  periodo: string
  estado: string
  firmado_por: string
  advertencias_aceptadas: number
}

/** `POST /filings/{period_id}/observe` */
export interface ObservacionRow {
  estado: string
  observado_por: string
}

/** `GET /filings/{period_id}/export` */
/* ==========================================================================
 * Utilidades minimas
 * ========================================================================== */

type Body = Record<string, unknown>

/* ==========================================================================
 * Formulario: `entity.pais`  ->  `Filing.form`
 * ==========================================================================
 * `period` no dice que formulario le corresponde. Y no es un olvido del
 * backend: en el modelo, un periodo genera N declaraciones (mensual de IGV-renta,
 * planilla, anual) y `/export` decide el formato al vuelo con `?formato=`.
 *
 * La pantalla tiene UNA columna "Formulario" y una fila por periodo, o sea que
 * asume 1:1. Se resuelve con lo unico cierto que hay: el PAIS de la entidad, que
 * si viene en la fila, y la declaracion mensual que le corresponde. Es una
 * etiqueta de presentacion derivada de un dato real, no un dato inventado — y
 * por eso un pais desconocido se muestra vacio en vez de asumir el peruano.
 * TODO(backend): un `formulario` (o una lista de declaraciones) por periodo.
 */
const FORMULARIO_MENSUAL: Record<string, string> = {
  PE: "FV 621 · IGV Renta mensual",
}

export function formularioDe(pais: string | null | undefined): string {
  return FORMULARIO_MENSUAL[(pais ?? "").toUpperCase()] ?? ""
}

/* ==========================================================================
 * Estado: `period.estado` + firma  ->  `FilingStatus`
 * ==========================================================================
 *   abierto      ->  en_preparacion
 *   en_revision  ->  listo_para_revision
 *   presentado   ->  presentado
 *   cerrado      ->  presentado
 *   (firmado_por no nulo)  ->  firmado    <- gana sobre el estado
 *
 * `firmado_por` se mira PRIMERO porque firmar y presentar son dos hechos
 * distintos y el backend los guarda en columnas distintas: `firmar` pone
 * estado='presentado' Y `firmado_por`, pero un periodo puede quedar firmado sin
 * que el contador haya presentado nada ante el fisco todavia. Mostrar "firmado"
 * cuando hay firma es lo que el contador necesita saber.
 *
 * `listo_para_revisar` (pendientes == 0) tambien promueve a
 * "listo_para_revision": es exactamente lo que significa el nombre del campo, y
 * sin eso todo periodo abierto se veria "en preparacion" aunque no le falte nada.
 *
 * SIN CONTRAPARTE: `observado`. El backend no tiene ese estado — `observe`
 *   DEVUELVE el periodo a 'abierto' y deja la observacion en `audit_log`. Asi que
 *   un periodo observado se lee "en_preparacion", que es donde efectivamente
 *   quedo. La SPA muestra el estado un momento en optimista tras observar y en el
 *   siguiente refetch vuelve a "en preparacion": es la verdad, no un bug.
 *   TODO(backend): `period.estado = 'observado'`, o exponer la ultima observacion.
 */
export function estadoFromApi(row: {
  estado: string
  firmado_por: string | null
  listo_para_revisar?: boolean
}): FilingStatus {
  if (row.firmado_por) return "firmado"
  if (row.estado === "presentado" || row.estado === "cerrado") return "presentado"
  if (row.estado === "en_revision" || row.listo_para_revisar) return "listo_para_revision"
  return "en_preparacion"
}

/* ==========================================================================
 * Severidad: `BLOCKER | WARNING | INFO`  ->  `alta | media | baja`
 * ==========================================================================
 * No es cosmetico: "alta" es lo que la pantalla usa para BLOQUEAR el boton de
 * firmar, y BLOCKER es exactamente lo que el backend rechaza con 409. Las dos
 * escalas tienen que decir lo mismo o la SPA deja intentar algo que el servidor
 * siempre va a negar.
 */
const SEVERIDADES: Record<string, FilingException["severity"]> = {
  BLOCKER: "alta",
  WARNING: "media",
  INFO: "baja",
}

export function severidadFromApi(severidad: string | null | undefined): FilingException["severity"] {
  return SEVERIDADES[(severidad ?? "").toUpperCase()] ?? "media"
}

/**
 * Una excepcion del backend -> la de la SPA.
 *
 *   severity <->  severidad     (ver tabla arriba)
 *   title    <->  mensaje
 *   detail   <->  detalle ?? accion
 *
 * `title`/`detail`: el backend manda `mensaje` (que paso), `detalle` (evidencia,
 * solo en CONFIANZA_BAJA) y `accion` (que hacer). La SPA tiene dos campos. Se
 * pone el mensaje como titulo y se compone el cuerpo con lo que haya: el
 * `accion` es la mitad util —"Resolver la cola de revision"— y tirarlo dejaria
 * una excepcion sin salida.
 *
 * SIN CONTRAPARTE en el backend:
 *   id — la excepcion es calculada, no una fila. Se compone con el codigo y el
 *     indice; solo se usa como `key`.
 *   documentRef / amount — el backend los mete DENTRO del texto del mensaje
 *     ("F001-123 clasificado con 62% de confianza", "diferencia 340.00"). No se
 *     parsean del texto: extraer numeros de una cadena formateada es la clase de
 *     codigo que se rompe el dia que alguien cambia una palabra. Quedan undefined.
 *     TODO(backend): `documento` y `monto` como campos de la excepcion.
 *   source — que capa la genero. Todas las excepciones las produce el propio
 *     backend con SQL, o sea "regla"; no hay ninguna que venga de un modelo. Se
 *     lee "regla" porque es cierto, no por defecto.
 *   resolved — no hay estado de resolucion: una excepcion existe mientras su
 *     condicion se cumpla y desaparece cuando se corrige. Se lee false siempre,
 *     que es lo mismo que decir "sigue abierta".
 */
export function excepcionFromApi(row: ExcepcionRow, index: number): FilingException {
  const cuerpo = [row.detalle, row.accion].filter(Boolean).join(" · ")
  return {
    id: `${row.codigo}-${index}`,
    severity: severidadFromApi(row.severidad),
    title: row.mensaje ?? "",
    detail: cuerpo,
    documentRef: undefined,
    amount: undefined,
    source: "regla",
    resolved: false,
  }
}

/* ==========================================================================
 * Bandeja  —  SPA `Filing`  <->  `GET /filings/inbox`
 * ==========================================================================
 *   Filing.id         <->  period.id
 *   Filing.tenantId   <->  tenant_id      (el del PERIODO, ver nota de arriba)
 *   Filing.tenantName <->  tenant         (nombre del tenant)
 *   Filing.ruc        <->  entity.tax_id
 *   Filing.period     <->  periodo        ("2026-01")
 *   Filing.status     <->  estado + firmado_por + listo_para_revisar
 *   Filing.preparedBy <->  cerrado_por
 *   Filing.signedBy   <->  firmado_por
 *   Filing.form        ->  derivado de entity.pais (ver `formularioDe`)
 *
 * OJO: `Filing.tenantName` muestra el nombre del TENANT ("Siete") y la fila trae
 * ademas la `razon_social` de la ENTIDAD, que es la sociedad concreta cuyo
 * periodo se declara. No son lo mismo: un tenant puede tener varias entidades y
 * entonces la bandeja mostraria N filas con el mismo nombre. Se prefiere
 * `razon_social` cuando existe —es lo que identifica de verdad la declaracion— y
 * el nombre del tenant queda como respaldo.
 * TODO(SPA): columnas separadas para tenant y entidad en la bandeja.
 *
 * `preparedBy` / `signedBy` son UUIDs de `app_user`, no nombres. La pantalla los
 * usa para dos cosas: mostrarlos, y comprobar la separacion de funciones
 * (`preparedBy === profile.displayName`). Un uuid nunca va a ser igual a un
 * nombre, asi que ESA COMPROBACION DEL CLIENTE NO PUEDE FUNCIONAR. No se
 * disimula: se conserva el uuid (es el dato real) y la pantalla se apoya en el
 * backend, que si sabe quien es quien y responde 403 — ahora visible via
 * `onWriteError`. La separacion de funciones nunca fue una regla del cliente.
 * TODO(backend): devolver el NOMBRE de quien preparo y de quien firmo.
 *
 * SIN CONTRAPARTE en el backend (la bandeja NO los tiene, y se leen vacios):
 *
 *   dueDate
 *     `period` no guarda vencimiento. El calendario de vencimientos existe en el
 *     backend (`app/modules/countries`, con su prueba `test_cronograma_sunat.py`)
 *     pero NO esta expuesto por HTTP. Calcularlo en la SPA seria replicar el
 *     cronograma de SUNAT —ultimo digito del RUC, dias habiles, feriados— en el
 *     cliente y desincronizarlo la primera vez que cambie una resolucion. Se lee
 *     "" y la pantalla muestra "sin cronograma" en vez de una fecha inventada o
 *     un "en NaNd".
 *     TODO(backend): `GET /filings/inbox` con `vence_el`, del cronograma que ya
 *     existe. Es el campo que mas falta: la bandeja se ORDENA por vencimiento.
 *
 *   igvDebito / igvCredito / igvAPagar / pagoACuenta
 *     La liquidacion del formulario no esta en `period` ni en los totales que
 *     devuelve el detalle (que son debe/haber por libro, no casillas del FV 621).
 *     Se leen 0. Un cero se ve como cero; un numero aproximado se firmaria.
 *     TODO(backend): la liquidacion del periodo en `/filings/{id}`.
 *
 *   balanced
 *     El cuadre por libro lo calcula `GET /filings/{id}` (`totales.por_libro[*]
 *     .cuadra`), no la bandeja. Se lee `true`: la fila se pinta en rojo cuando
 *     `balanced` es false, y pintar de rojo TODA la cartera porque no lo sabemos
 *     es una alarma falsa que enseña a ignorar la alarma. El descuadre real sigue
 *     bloqueando: el backend lo devuelve como BLOCKER al firmar y rechaza con 409.
 *     TODO(backend): `cuadra` en la bandeja.
 *
 *   diff
 *     El diff contra el mes anterior solo esta en el detalle. Lista vacia.
 *
 * DERIVADO Y NO INVENTADO — la excepcion de documentos sin contabilizar:
 *   La bandeja SI trae `pendientes`. El propio backend, en `_excepciones()`,
 *   convierte ese mismo numero en una excepcion BLOCKER
 *   ("N comprobantes sin contabilizar" -> "Resolver la cola de revision"). Aqui
 *   se aplica la MISMA regla sobre el MISMO dato, con el mismo texto, para que la
 *   bandeja pueda decir por que una declaracion no esta lista y para que el boton
 *   de firmar se bloquee igual que lo hara el servidor. No es una excepcion
 *   fabricada: es la del backend, calculada un paso antes.
 */
export function filingFromApi(row: FilingInboxRow): Filing {
  const exceptions: FilingException[] = []
  if (row.pendientes > 0) {
    exceptions.push({
      id: `DOCUMENTOS_SIN_CONTABILIZAR-${row.id}`,
      severity: "alta",
      title: `${row.pendientes} comprobantes sin contabilizar.`,
      detail: "Resolver la cola de revision.",
      source: "regla",
      resolved: false,
    })
  }

  return {
    id: row.id,
    tenantId: row.tenant_id,
    tenantName: row.razon_social || row.tenant || "",
    ruc: row.tax_id ?? "",
    period: row.periodo,
    form: formularioDe(row.pais),
    dueDate: "",
    status: estadoFromApi(row),
    preparedBy: row.cerrado_por ?? "",
    signedBy: row.firmado_por ?? undefined,
    igvDebito: 0,
    igvCredito: 0,
    igvAPagar: 0,
    pagoACuenta: 0,
    exceptionsCount: exceptions.length,
    balanced: true,
    exceptions,
    diff: [],
  }
}

/* ==========================================================================
 * Detalle  —  SPA `FilingDetail`  <->  `GET /filings/{period_id}`
 * ==========================================================================
 * La bandeja es cross-tenant y barata: una fila por periodo, sin tocar los
 * asientos. El detalle es la segunda consulta, por el periodo que se abrio, y es
 * la unica que sabe tres cosas que no se pueden deducir de la bandeja:
 *
 *   totales.por_libro       -> `bookTotals`  (debe/haber/asientos/cuadra por libro)
 *   excepciones             -> `exceptions`  (las reales, calculadas con SQL)
 *   diff_mes_anterior       -> `diff`        (variacion contra el mes anterior)
 *
 * DECIMALES COMO TEXTO. `debe`/`haber` salen de un `SUM()` sobre `NUMERIC` y
 * pueden llegar como numero o como cadena segun serialice el backend. Se pasan
 * por `numero()` en vez de confiar en uno de los dos: un `"1234.50"` sumado como
 * cadena da "01234.50" y nadie lo nota hasta que el total esta mal.
 *
 * EL PORCENTAJE CAMBIA DE ESCALA. El backend manda `variacion_pct` en PUNTOS
 * (-62.6 = bajo 62,6%). `formatPercent` usa `Intl` con `style: "percent"`, que
 * espera una FRACCION (-0.626) y multiplica por cien al mostrar. Sin dividir,
 * una caida del 62% se veria como "-6.260,0 %". Se divide aqui, en el mapeo, que
 * es donde viven las diferencias de forma entre los dos lados.
 *
 * CUADRE: `balanced` es de TODO el periodo (todos los libros cuadran) y cada
 * libro trae el suyo. Un periodo SIN asientos da `balanced: true` de forma
 * trivial —no hay nada que descuadre— y eso, dicho solo, es enganoso: se
 * acompana con `hasEntries` para que la pantalla pueda decir "sin asientos" en
 * vez de "libros cuadrados", que es lo que un contador leeria como "revisado".
 *
 * SIN CONTRAPARTE en el backend (siguen sin poder llenarse desde aqui):
 *   igvDebito / igvCredito / igvAPagar / pagoACuenta — el detalle devuelve
 *     debe/haber por LIBRO, no las casillas del FV 621. La liquidacion del
 *     formulario no la calcula ningun endpoint todavia. Siguen en 0.
 *     TODO(backend): la liquidacion del periodo en `/filings/{id}`.
 *   dueDate — el cronograma sigue sin estar expuesto por HTTP.
 */

/** `book.tipo` del backend -> etiqueta. Los tres libros del motor. */
const LIBROS: Record<string, string> = {
  // Ver `BOOK_LABELS` en `lib/types.ts`: los tres nombran a su destinatario.
  statutory: "Societario",
  tax: "Tributario",
  management: "Gerencial",
}

export function etiquetaLibro(tipo: string): string {
  return LIBROS[(tipo ?? "").toLowerCase()] ?? tipo
}

/**
 * Un `NUMERIC` de Postgres, que puede llegar como numero o como cadena.
 * Devuelve 0 ante null/undefined/"" y ante cualquier cosa que no sea un numero
 * finito: un `NaN` en una pantalla contable se propaga a todos los totales.
 */
function numero(v: string | number | null | undefined): number {
  if (v === null || v === undefined || v === "") return 0
  const n = typeof v === "number" ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

export function filingDetailFromApi(row: FilingDetailRow): FilingDetail {
  const porLibro = row.totales?.por_libro ?? {}

  const bookTotals: FilingBookTotal[] = Object.entries(porLibro).map(([tipo, t]) => ({
    book: tipo,
    label: etiquetaLibro(tipo),
    debit: numero(t?.debe),
    credit: numero(t?.haber),
    entries: t?.asientos ?? 0,
    balanced: Boolean(t?.cuadra),
  }))
  // Orden estable y con sentido contable: el legal primero, el gerencial al
  // final. `Object.entries` sigue el orden del JSON, que es el del GROUP BY.
  const ORDEN = ["statutory", "tax", "management"]
  bookTotals.sort((a, b) => {
    const ia = ORDEN.indexOf(a.book)
    const ib = ORDEN.indexOf(b.book)
    return (ia < 0 ? ORDEN.length : ia) - (ib < 0 ? ORDEN.length : ib)
  })

  const diff: FilingDiffRow[] = Object.entries(row.diff_mes_anterior ?? {}).map(
    ([tipo, d]) => {
      const previous = numero(d?.anterior)
      const current = numero(d?.actual)
      return {
        concept: etiquetaLibro(tipo),
        previous,
        current,
        delta: current - previous,
        // Puntos -> fraccion. Ver la nota de arriba.
        deltaPct: d?.variacion_pct === null || d?.variacion_pct === undefined
          ? null
          : d.variacion_pct / 100,
        note: d?.atencion
          ? "Variacion de 30% o mas: el motor la marca para que se mire antes de firmar."
          : undefined,
      }
    },
  )
  diff.sort((a, b) => a.concept.localeCompare(b.concept))

  const documentCounts = Object.entries(row.totales?.documentos ?? {})
    .map(([estado, n]) => ({ status: estado, count: n ?? 0 }))
    .sort((a, b) => b.count - a.count)

  return {
    period: row.periodo,
    entityName: row.entidad?.razon_social ?? "",
    taxId: row.entidad?.tax_id ?? "",
    country: row.entidad?.pais ?? "",
    status: estadoFromApi({ estado: row.estado, firmado_por: row.firmado_por }),
    preparedBy: row.preparado_por ?? null,
    signedBy: row.firmado_por ?? null,
    bookTotals,
    documentCounts,
    balanced: bookTotals.every((b) => b.balanced),
    hasEntries: bookTotals.some((b) => b.entries > 0),
    exceptions: (row.excepciones ?? []).map(excepcionFromApi),
    diff,
  }
}

/* ==========================================================================
 * Firmar  —  `POST /filings/{period_id}/sign`
 * ==========================================================================
 *   reason  <->  FirmaIn.motivo               (min_length 3)
 *   —        ->  FirmaIn.forzar_advertencias
 *
 * La SPA mandaba `{reason}` y el backend pide `motivo`: 422 en cada firma.
 *
 * `forzar_advertencias` es una decision de producto y conviene que quede escrita.
 * El backend distingue dos niveles: los BLOCKER nunca se saltan (409, sin
 * excepcion posible) y los WARNING se pueden aceptar explicitamente —su mensaje
 * es literalmente "Revisa y vuelve a firmar confirmando"—.
 *
 * El dialogo de firma de la SPA ES esa confirmacion explicita: se abre desde una
 * fila cuyo detalle lista las excepciones, exige escribir un motivo, y solo se
 * habilita si no hay excepciones de severidad alta abiertas. Alguien que llega
 * ahi ya reviso y ya se hizo responsable por escrito. Por eso se manda `true`
 * por defecto: mandar `false` produciria un 409 del que el usuario no tiene
 * ninguna forma de salir desde la pantalla, y un boton que nunca funciona es
 * peor control que ninguno — se aprende a ignorarlo.
 *
 * Los BLOCKER siguen bloqueando en los dos lados: la SPA no habilita el boton
 * (severidad "alta") y el backend rechaza igual si algo cambio en el medio.
 * Se deja como parametro para que un futuro checkbox "acepto las advertencias"
 * pueda mandar `false` sin tocar el mapeo.
 */
export function signToApi(reason: string, aceptarAdvertencias = true): Body {
  return { motivo: reason, forzar_advertencias: aceptarAdvertencias }
}

export function signFromApi(row: FirmaRow): {
  period: string
  status: FilingStatus
  signedBy: string
  acceptedWarnings: number
} {
  return {
    period: row.periodo,
    status: estadoFromApi({ estado: row.estado, firmado_por: row.firmado_por }),
    signedBy: row.firmado_por ?? "",
    acceptedWarnings: row.advertencias_aceptadas ?? 0,
  }
}

/* ==========================================================================
 * Observar  —  `POST /filings/{period_id}/observe`
 * ==========================================================================
 *   reason  <->  ObservacionIn.motivo   (min_length 3)
 *   —        ->  ObservacionIn.detalle  (opcional; la SPA solo tiene un campo)
 *
 * `detalle` no se manda: duplicar el motivo en las dos claves no agrega nada al
 * audit log. Si algun dia el dialogo separa "motivo" de "que hay que corregir",
 * el segundo va aqui.
 */
export function observeToApi(reason: string): Body {
  return { motivo: reason }
}

export function observeFromApi(row: ObservacionRow): { status: FilingStatus; observedBy: string } {
  return {
    status: estadoFromApi({ estado: row.estado, firmado_por: null }),
    observedBy: row.observado_por ?? "",
  }
}

/* ==========================================================================
 * Exportar  —  `GET /filings/{period_id}/export`
 * ==========================================================================
 * La SPA esperaba `{ url }` — un archivo para descargar. El backend NO devuelve
 * un archivo: devuelve el ESTADO de la exportacion (si el periodo esta listo
 * para presentar, y si no, por que) junto con las excepciones y los totales. El
 * archivo del PLE / FV 621 todavia no se genera por HTTP.
 *
 * Se mapea lo que si hay, que es justamente lo que el contador necesita antes de
 * exportar: "listo" o "no, y esta es la razon". El boton deja de prometer una
 * descarga que nunca llega y pasa a decir la verdad.
 * TODO(backend): que `?formato=ple|fv621` devuelva el archivo (o una URL firmada).
 *
 *   ready       <->  listo_para_presentar
 *   blockedWhy  <->  motivo_si_no
 *   exceptions  <->  excepciones (misma traduccion de severidad)
 */
/* ==========================================================================
 * El 409 de la firma: aplanar un detalle que NO es un string
 * ==========================================================================
 * `writeErrors.ts` sabe explicar cualquier estado HTTP, pero para el 409 usa el
 * `detail` solo si es texto. Y `POST /sign` es el unico endpoint del sistema que
 * responde un OBJETO como detalle:
 *
 *   {"mensaje": "No se puede firmar con excepciones bloqueantes.",
 *    "excepciones": [{severidad, codigo, mensaje, accion}, ...]}
 *
 * Sin aplanarlo, el usuario ve el 409 generico ("el periodo puede estar
 * cerrado") mientras el servidor le esta diciendo exactamente que le falta. Esto
 * lo convierte en una linea legible que `reportWriteError` si puede mostrar.
 * Traducir la forma del error tambien es traducir: por eso vive en el mapeo.
 */
export function mensajeDeConflicto(detail: unknown): string | null {
  if (!detail || typeof detail !== "object") return null
  const d = detail as { mensaje?: unknown; excepciones?: unknown }
  if (typeof d.mensaje !== "string") return null

  const excepciones = Array.isArray(d.excepciones) ? (d.excepciones as ExcepcionRow[]) : []
  if (excepciones.length === 0) return d.mensaje

  const detalles = excepciones
    .map((e) => [e.mensaje, e.accion].filter(Boolean).join(" "))
    .filter(Boolean)
    .join(" | ")
  return detalles ? `${d.mensaje} ${detalles}` : d.mensaje
}
